// Category colours: data that names a kind (a Spark stage, a tour, a status), rendered through Tag
// or a tinted fill. One family, derived in OKLCH: every hue at one lightness and one chroma per
// role, so no category reads louder than another and hue alone says which. Gold is the accent and
// never a category. docs/operations/design-standard.md, "Colour and contrast".

export interface Swatch {
  /** For CSS: inline styles, and the oklch() written into a Tailwind arbitrary class. */
  oklch: string;
  /** The same colour in sRGB, for anything that parses or concatenates it (Tag's tint(), canvas). */
  hex: string;
}

export interface CategoryColor {
  /** Text, a tag's colour, a bar or a dot: clears APCA Lc 60 on every surface step. */
  ink: Swatch;
  /** A fill or a rule under ink text, always at partial alpha. Never text. */
  mark: Swatch;
}

/**
 * One lightness and one chroma per role. Chroma is a ceiling: where sRGB cannot reach it at this
 * lightness (reds, blues and violets), the hue takes the most it can, so the oklch() and the hex
 * name the same colour on every screen.
 */
export const INK = { l: 0.83, c: 0.13 } as const;
export const MARK = { l: 0.7, c: 0.16 } as const;

/** OKLCH hue per name, about 22 degrees apart, so neighbours stay apart at matched lightness. */
export const HUES = {
  rose: 4,
  red: 25,
  orange: 50,
  amber: 72,
  yellow: 94,
  lime: 122,
  green: 148,
  emerald: 168,
  teal: 188,
  cyan: 210,
  sky: 232,
  blue: 254,
  indigo: 276,
  purple: 300,
  fuchsia: 322,
  pink: 344,
} as const;

export type CategoryHue = keyof typeof HUES;
export type CategoryName = CategoryHue | 'neutral';

/** OKLCH to linear sRGB (Ottosson's matrices). */
const linear = (l: number, c: number, h: number): [number, number, number] => {
  const a = c * Math.cos((h * Math.PI) / 180);
  const b = c * Math.sin((h * Math.PI) / 180);
  const L = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const M = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const S = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * L - 3.3077115913 * M + 0.2309699292 * S,
    -1.2684380046 * L + 2.6097574011 * M - 0.3413193965 * S,
    -0.0041960863 * L - 0.7034186147 * M + 1.707614701 * S,
  ];
};

const inGamut = (rgb: number[]) => rgb.every((v) => v >= -1e-6 && v <= 1 + 1e-6);

const encode = (v: number): string => {
  const x = Math.min(1, Math.max(0, v));
  const e = x <= 0.0031308 ? 12.92 * x : 1.055 * x ** (1 / 2.4) - 0.055;
  return Math.round(e * 255).toString(16).padStart(2, '0');
};

/** An OKLCH colour as sRGB hex, clamped. */
export const oklchToHex = (l: number, c: number, h: number): string => `#${linear(l, c, h).map(encode).join('')}`;

const swatch = (role: { l: number; c: number }, hue: number | null): Swatch => {
  if (hue === null) return { oklch: `oklch(${role.l} 0 0)`, hex: oklchToHex(role.l, 0, 0) };
  // Whole thousandths, so the chroma written into the oklch() is exactly the one that was checked.
  let k = Math.round(role.c * 1000);
  while (k > 0 && !inGamut(linear(role.l, k / 1000, hue))) k -= 1;
  const c = k / 1000;
  return { oklch: `oklch(${role.l} ${c} ${hue})`, hex: oklchToHex(role.l, c, hue) };
};

const colour = (hue: number | null): CategoryColor => ({ ink: swatch(INK, hue), mark: swatch(MARK, hue) });

export const CATEGORY = Object.fromEntries([
  ...Object.entries(HUES).map(([name, hue]) => [name, colour(hue)]),
  ['neutral', colour(null)],
]) as Record<CategoryName, CategoryColor>;

/** A swatch at partial alpha, as CSS: `oklch(0.7 0.16 25 / 0.15)`. */
export const alpha = (s: Swatch, a: number): string => s.oklch.replace(/\)$/, ` / ${a})`);

// Measuring sticks for anyone choosing or checking a category colour.

const channels = (hex: string): number[] => {
  const n = parseInt(hex.replace('#', ''), 16);
  return [n >> 16, (n >> 8) & 255, n & 255];
};

/** `hex` at `a` over `ground`, composited the way the browser paints an alpha fill. */
export const over = (hex: string, ground: string, a: number): string => {
  const top = channels(hex);
  const base = channels(ground);
  return `#${base.map((v, i) => Math.round(v + (top[i] - v) * a).toString(16).padStart(2, '0')).join('')}`;
};

/** APCA lightness contrast (0.0.98G) of light text on a darker ground, as a positive Lc. */
export const contrast = (text: string, ground: string): number => {
  const y = (hex: string) => {
    const [r, g, b] = channels(hex).map((v) => (v / 255) ** 2.4);
    const lum = 0.2126729 * r + 0.7151522 * g + 0.072175 * b;
    return lum > 0.022 ? lum : lum + (0.022 - lum) ** 1.414;
  };
  const s = (y(text) ** 0.62 - y(ground) ** 0.65) * 1.14;
  return s < 0.1 ? 0 : (s - 0.027) * 100;
};

/** Euclidean distance in OKLab. Side by side, two category colours under about 0.04 read as one. */
export const distance = (a: string, b: string): number => {
  const lab = (hex: string) => {
    const [r, g, bl] = channels(hex).map((v) => {
      const x = v / 255;
      return x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
    });
    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * bl);
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * bl);
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * bl);
    return [
      0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
      1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
      0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
    ];
  };
  const p = lab(a);
  const q = lab(b);
  return Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]);
};
